import React, { useEffect, useMemo, useState } from "react";

import { Field, FieldLabel } from "@/components/ui/field";
import { FieldMultiSelectFullWidth } from "@/components/trade-filter-selects";
import { STRATEGY_PINE_SECTION_LABEL_CLASS } from "@/components/Strategies/StrategyPineSectionCard";
import { cn } from "@/lib/utils";
import { listStrategyRdConditionChoices } from "../../../services/strategyRdApi";
import {
  STRATEGY_RD_CONDITION_MAX_DEFAULT,
  strategyRdConditionOptionGroups,
  strategyRdNormalizeConditionPicks,
  type StrategyRdConditionChoices,
} from "../../../utils/strategyRdConditionSetup";
import { strategyRdMetaClassName } from "./strategyRdCardShell";

let choicesPromise: Promise<StrategyRdConditionChoices> | null = null;

/** GET /setups/condition-choices once per page load (retried after a failure). */
export function useStrategyRdConditionChoices(enabled = true): {
  data: StrategyRdConditionChoices | null;
  error: string | null;
} {
  const [data, setData] = useState<StrategyRdConditionChoices | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    choicesPromise = choicesPromise ?? listStrategyRdConditionChoices();
    choicesPromise.then(
      (res) => {
        if (!cancelled) setData(res);
      },
      (err: unknown) => {
        choicesPromise = null;
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load conditions.");
      }
    );
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return { data, error };
}

/** "Condition turns true": pick 1–3 conditions that must all become true on the same bar. */
export function StrategyRdSetupConditionsField({
  value,
  onChange,
  className,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  className?: string;
}): JSX.Element {
  const { data, error } = useStrategyRdConditionChoices();
  const max = data?.maxConditions ?? STRATEGY_RD_CONDITION_MAX_DEFAULT;
  const groups = useMemo(() => strategyRdConditionOptionGroups(data?.choices ?? []), [data]);
  const options = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  return (
    <Field
      className={cn("min-w-[14rem] max-w-[26rem] flex-1", className)}
      data-strategy-rd-setup-conditions=""
      title="Pick 1–3 conditions. The setup fires on the bar where they all turn true together (one per indicator)."
    >
      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
        Conditions ({value.length}/{max})
      </FieldLabel>
      <FieldMultiSelectFullWidth
        id="strategy-rd-setup-conditions"
        selected={value}
        onChange={(next) => onChange(strategyRdNormalizeConditionPicks(next, value, max))}
        options={options}
        optionGroups={groups}
        placeholder={data ? "e.g. RSI · Overbought" : error ? "Conditions unavailable" : "Loading…"}
        disabled={!data}
      />
      {/* No helper line under the select: the row aligns its fields on their bottom edge, and a
          taller field pushed this select above the others. The setup's description under the row
          says how conditions fire; only a load error is shown here. */}
      {error ? <p className={cn(strategyRdMetaClassName, "text-xs text-destructive")}>{error}</p> : null}
    </Field>
  );
}
